/**
 * Per-capability visual state map -- mirrors the constellation's
 * `nodeState.ts` so the dossier + SPA show the same picture.
 *
 * A capability is impacted when EITHER its own id appears in the
 * cascade (a direct capability impact) OR any IBS it supports
 * (`linked_ibs_ids`) is impacted -- "inherit from linked IBS".
 * This is the seam that makes the Live cascade lens light up:
 * findings in v2 land on `ibs_service` / `service`, never on
 * `capability`, so without the inheritance pass NOTHING on the
 * map ever flags (P88.2 bugfix).
 *
 * Default: baselineFor(cell) -- at-risk when open_finding_count > 0,
 * else stable. Incident origin -> down; other impacted -> degraded.
 *
 * Used by the cascade tree builder, the cell render, and the
 * popover. Pure helper; no React.
 */
import type {
  CapabilityCell,
  CapabilityDependencyEdge,
  CascadeSummary,
} from "shared/api/types";
import { reachableDownstream } from "twin/capmap/litSet";

export type CapabilityState = "down" | "degraded" | "at-risk" | "stable";

/** Severity rank for picking the WORST impact driving a cell. */
const SEVERITY_RANK: Record<string, number> = {
  critical: 3,
  high: 2,
  medium: 1,
  low: 0,
};

/** Map every cascade impact's entity_id -> its headline severity.
 * Flat over ALL kinds: capability ids match cells directly, IBS
 * ids match a cell's `linked_ibs_ids`. Worst severity wins when an
 * id appears more than once. */
function impactSeverityById(cascade: CascadeSummary): Map<string, string> {
  const m = new Map<string, string>();
  for (const impact of cascade.impacted) {
    const sev = impact.headline_finding.severity;
    const prev = m.get(impact.entity_id);
    if (
      prev === undefined ||
      (SEVERITY_RANK[sev] ?? -1) > (SEVERITY_RANK[prev] ?? -1)
    ) {
      m.set(impact.entity_id, sev);
    }
  }
  return m;
}

/** Is this cell impacted -- directly or via a linked IBS? Returns
 * the WORST severity across its direct impact + every linked-IBS
 * impact, or null if untouched. */
function cellWorstSeverity(
  cell: CapabilityCell,
  bySeverity: Map<string, string>,
): string | null {
  let worst: string | null = null;
  const consider = (s: string | undefined) => {
    if (s === undefined) return;
    if (worst === null || (SEVERITY_RANK[s] ?? -1) > (SEVERITY_RANK[worst] ?? -1)) {
      worst = s;
    }
  };
  consider(bySeverity.get(cell.id));
  for (const ibsId of cell.linked_ibs_ids) consider(bySeverity.get(ibsId));
  return worst;
}

/** The incident origin cell -- a critical-driven lit capability,
 * chosen to root the calm cascade tree at the DEEPEST upstream
 * cause: among the critical candidates, the one whose downstream
 * dependency reach covers the most lit cells (tie-break: smallest
 * id, deterministic). Shared by buildStateMap + pickIncidentOrigin
 * so the "down" cell and the tracing-tree root always agree.
 *
 * `edges` is optional: with none, downstream reach is just the
 * seed, so coverage is 1 for every candidate and the smallest-id
 * candidate wins -- the pre-P89 behaviour. The view passes the
 * real dependency edges to get the multi-hop chain. Null when no
 * critical cause maps onto a cell. */
export function findOrigin(
  cells: CapabilityCell[],
  cascade: CascadeSummary | null | undefined,
  edges: CapabilityDependencyEdge[] = [],
): string | null {
  if (!cascade) return null;
  const bySeverity = impactSeverityById(cascade);

  const lit = new Set<string>();
  const candidates: string[] = [];
  for (const cell of cells) {
    const sev = cellWorstSeverity(cell, bySeverity);
    if (sev === null) continue;
    lit.add(cell.id);
    if (sev === "critical") candidates.push(cell.id);
  }
  if (candidates.length === 0) return null;

  let best: string | null = null;
  let bestCoverage = -1;
  for (const id of candidates) {
    const reach = reachableDownstream(id, edges);
    let coverage = 0;
    for (const r of reach) if (lit.has(r)) coverage++;
    if (
      coverage > bestCoverage ||
      (coverage === bestCoverage && best !== null && id < best)
    ) {
      best = id;
      bestCoverage = coverage;
    }
  }
  return best;
}

export function buildStateMap(
  cells: CapabilityCell[],
  cascade: CascadeSummary | null | undefined,
  edges: CapabilityDependencyEdge[] = [],
): Map<string, CapabilityState> {
  const out = new Map<string, CapabilityState>();
  for (const c of cells) {
    out.set(c.id, c.open_finding_count > 0 ? "at-risk" : "stable");
  }
  if (!cascade) return out;

  const bySeverity = impactSeverityById(cascade);
  const originId = findOrigin(cells, cascade, edges);
  for (const cell of cells) {
    if (cell.id === originId) {
      out.set(cell.id, "down");
      continue;
    }
    if (cellWorstSeverity(cell, bySeverity) !== null) {
      out.set(cell.id, "degraded");
    }
  }
  return out;
}

/** Locate the incident origin -- thin wrapper over `findOrigin`
 * kept as the view's named entry point. */
export function pickIncidentOrigin(
  cells: CapabilityCell[],
  cascade: CascadeSummary | null | undefined,
  edges: CapabilityDependencyEdge[] = [],
): string | null {
  return findOrigin(cells, cascade, edges);
}
